import { z } from 'zod';
import { getFlowError } from '../constants/flowErrors.js';

// Firebase timestamp schema
const FirebaseTimestampSchema = z.object({
  seconds: z.number(),
  nanoseconds: z.number()
});

// Network info schema for ethernet/wifi
const NetworkInfoSchema = z.union([
  z.array(z.any()), // Array format
  z.string(),       // String format (empty string)
  z.null()
]).optional();

// Status info schema for unit technical details
const StatusInfoSchema = z.object({
  _ethernet_ip: NetworkInfoSchema,
  _wifi_ip: NetworkInfoSchema,
  _software_version: z.string().optional(),
  _hardware_version: z.string().optional(),
  _cartridge_firmware_version: z.string().optional(),
  _cartridge_hardware_version: z.string().optional(),
  _forced_waterflush_readings: z.boolean().optional(),
  _periodic_waterflush_active: z.boolean().optional(),
  _liquid_sensor_active: z.boolean().optional(),
  _water_flush_button_active: z.boolean().optional(),
  _out_of_order_active: z.boolean().optional(),
  _welcome_screen_texts_visible: z.boolean().optional(),
  _wizard_is_done: z.boolean().optional(),
  _score_screen_version: z.string().optional(),
  _screen_serial_number: z.union([z.string(), z.number()]).optional(),
  _sensor_serial_number: z.union([z.string(), z.number()]).optional()
  // Passthrough (not strict): the device firmware adds new statusInfo fields
  // ahead of the frontend, and a strict object would fail validateUnit() for
  // the whole unit over one unlisted key — removing it from the dashboard
  // entirely. Unknown fields are simply ignored instead.
}).passthrough().optional();

// Post-assembly calibration schema
const PostAssemblyCalibrationSchema = z.union([
  z.object({
    conductivity: z.object({
      currentCalibrationFactor: z.number().optional(),
      dynamicMode: z.boolean().optional(),
      enable: z.boolean(),
      manualScalarFactor: z.number()
    }).strict()
  }).strict(),
  z.null(),
  z.undefined()
]).optional();

// Scheduled water flush schema
const ScheduledWFSchema = z.union([
  z.object({
    enabled: z.boolean(),
    iterations: z.number().int().min(0),
    time: z.string().regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/), // HH:MM format
    weekDays: z.object({
      Monday: z.boolean().optional(),
      Tuesday: z.boolean().optional(),
      Wednesday: z.boolean().optional(),
      Thursday: z.boolean().optional(),
      Friday: z.boolean().optional(),
      Saturday: z.boolean().optional(),
      Sunday: z.boolean().optional()
    }).strict().optional()
  }).strict(),
  z.null(),
  z.undefined() // Allow undefined for new units
]).optional();

// Main unit schema
export const UnitSchema = z.object({
  // Core identification - required
  unit: z.union([z.string().min(1), z.number()]),
  center: z.string().min(1),
  organization: z.string().min(1),
  
  // Required operational fields
  unit_status: z.enum(['active', 'disabled']),
  daily_unit_use: z.number().min(0),
  
  // Make these fields optional with defaults
  isUnitDisconnected: z.boolean().optional().default(false),
  isCartridgeDisconnected: z.boolean().optional().default(false),
  rosterActive: z.boolean().optional().default(false),
  isSmartActionsEnabled: z.boolean().optional().default(false),
  isSit: z.boolean().optional().default(false),
  isUnitStuck: z.boolean().optional().default(false),
  clogged: z.boolean().optional().default(false),
  
  // Optional fields
  description: z.string().optional(),
  total_kpod_use: z.union([z.number().min(0), z.string()]).optional(),
  kpod_id: z.string().optional(),
  display_id: z.string().optional(),
  lastLoginTimestamp: z.union([z.string(), z.number()]).optional(),
  lastInitTimestamp: z.union([z.string(), z.number()]).optional(),
  dailyUptime: z.union([z.number(), z.string()]).optional(),
  hasErrors: z.boolean().optional(),
  
  // Conditional fields (only present when relevant)
  time: z.number().optional(),
  since_last_score: z.union([z.number(), z.string()]).optional(),
  time_installed: FirebaseTimestampSchema.optional(),
  water_reference_ts: z.union([FirebaseTimestampSchema, z.string(), z.number()]).optional(),
  statusColor: z.enum(['green', 'yellow', 'red', 'gray']).optional(),
  
  // Complex objects
  statusInfo: StatusInfoSchema,
  scheduledWF: ScheduledWFSchema,
  postAssemblyCalibration: PostAssemblyCalibrationSchema,
  parameters: z.record(z.string(), z.any()).optional()
}).strict();

// Type inference
export type Unit = z.infer<typeof UnitSchema>;

/**
 * Determines if a unit has errors based on various conditions
 * @param {Unit} unit - The unit object
 * @param {Object} classifiedIssuesbyUnit - Issues data grouped by unit
 * @returns {boolean} - True if the unit has errors
 */
export const calculateHasErrors = (unit, classifiedIssuesbyUnitAndKey) => {
  const unitId = getUnitProperty(unit, 'unit');
  if (!unitId) return false;
  
  // Get the grouped issues for this unit
  const unitIssuesGrouped = classifiedIssuesbyUnitAndKey[unitId];
  
  // If no issues exist for this unit, return false
  if (!unitIssuesGrouped || Object.keys(unitIssuesGrouped).length === 0) {
    return false;
  }
  
  // Every issue in the live collection is open - closed ones are archived elsewhere
  return Object.values(unitIssuesGrouped).some(group => group.length > 0);
};


/**
 * Updates a unit object with the calculated hasErrors field
 * @param {Unit} unit - The unit object
 * @param {Object} classifiedIssuesbyUnit - Issues data grouped by unit
 * @returns {Unit} - Unit with updated hasErrors field
 */
export const updateUnitWithErrorStatus = (unit, classifiedIssuesbyUnit = {}) => {
  return {
    ...unit,
    hasErrors: calculateHasErrors(unit, classifiedIssuesbyUnit)
  };
};

/**
 * Validates if an object matches the Unit schema
 * @param {any} unit - The unit object to validate
 * @returns {boolean} - True if the unit is valid
 */
export const validateUnit = (unit) => {
  try {
    // Add hasErrors if missing before validation
    const unitWithErrors = unit?.hasErrors !== undefined ? unit : {
      ...unit,
      hasErrors: calculateHasErrors(unit)
    };
    
    UnitSchema.parse(unitWithErrors);
    return true;
  } catch (error) {
    console.group('🚨 Unit Validation Failed');
    console.log('Unit data:', unit);
    console.log('Validation errors:');

    // Zod v4 renamed ZodError.errors to ZodError.issues.
    const issues = error.issues || error.errors;
    if (Array.isArray(issues)) {
      issues.forEach(err => {
        const path = err.path?.join('.') || 'unknown';
        console.log(`❌ Field: ${path}`);
        console.log(`   Expected: ${err.expected || 'unknown'}`);
        console.log(`   Message: ${err.message || 'Unknown error'}`);
      });
    } else {
      console.log('Raw error:', error);
    }

    console.groupEnd();
    return false;
  }
};

/**
 * Safely parses and validates a unit object
 * @param {any} unit - The unit object to parse
 * @returns {Unit|null} - Parsed unit or null if invalid
 */
export const parseUnit = (unit) => {
  try {
    return UnitSchema.parse(unit);
  } catch (error) {
    console.error('Unit parsing failed:', error.issues || error.errors);
    return null;
  }
};

/**
 * Safely gets a property from a unit object using dot notation
 * @param {Unit} unit - The validated unit object
 * @param {string} propertyPath - Dot notation path
 * @param {any} fallback - Fallback value
 * @returns {any} - The property value or fallback
 */
export const getUnitProperty = (unit, propertyPath, fallback = null) => {
  if (propertyPath === 'hasErrors') {
    return unit?.hasErrors !== undefined ? unit.hasErrors : (calculateHasErrors(unit) || false);
  }

  if (!unit) return fallback;

  const keys = propertyPath.split('.');
  let value = unit;
  for (const key of keys) {
    if (value === null || value === undefined) return fallback;
    value = value[key];
  }
  return value !== undefined ? value : fallback;
};

/**
 * Checks if a unit is the backend unit
 * @param {Unit} unit - The unit object
 * @returns {boolean} - True if the unit is the backend unit
 */
export const isBackendUnit = (unit) => {
  if (!unit) return false;
  return unit.unit === 'backend';
};

/**
 * Gets the unit's current status based on various conditions
 * @param {Unit} unit - The unit object
 * @param {Array} issues - Array of issues for the unit (optional)
 * @returns {string} - The current status
 */
export const getUnitStatus = (unit, issues = []) => {
  if (!unit) return 'unknown';

  // Special handling for backend unit - only disconnected status is relevant
  if (unit.unit === 'backend') {
    return unit.isUnitDisconnected ? 'disconnected' : 'active';
  }
  
  if (unit.unit_status === 'disabled') {
    return 'disabled';
  }
  
  if (unit.isUnitDisconnected && unit.unit_status !== 'disabled') {
    return 'disconnected';
  }
  
  if (unit.clogged === true) {
    return 'clogged';
  }
  
  // Check for flow errors (replaces hardcoded FE16 check)
  const hasLoopedIssue = issues.some(issue => {
    const flowError = getFlowError(issue?.Issue?.ID);
    return flowError?.type === 'looped';
  });
  if (hasLoopedIssue) {
    return 'looped';
  }

  if (unit.isUnitStuck === true) {
    return 'stuck';
  }

  // Check for no sensor error
  const hasNoSensorIssue = issues.some(issue => {
    const flowError = getFlowError(issue?.Issue?.ID);
    return flowError?.type === 'no_sensor';
  });
  if (hasNoSensorIssue) {
    return 'no_sensor';
  }

  // Check for new sensor error
  const hasNewSensorIssue = issues.some(issue => {
    const flowError = getFlowError(issue?.Issue?.ID);
    return flowError?.type === 'new_sensor';
  });
  if (hasNewSensorIssue) {
    return 'new_sensor';
  }

  // Check for no water error
  const hasNoWater = issues.some(issue => {
    const flowError = getFlowError(issue?.Issue?.ID);
    return flowError?.type === 'no_water';
  });
  if (hasNoWater) {
    return 'no_water';
  }
  
  if (unit.daily_unit_use === 0 || unit.daily_unit_use === '') {
    return 'unused';
  }
  
  return 'active';
};

/**
 * Formats a Firebase timestamp from the unit data
 * @param {Object} timestamp - Firebase timestamp object
 * @returns {string} - Formatted timestamp or '-' if invalid
 */
export const formatUnitTimestamp = (timestamp) => {
  if (!timestamp || typeof timestamp !== 'object' || !timestamp.seconds) {
    return '-';
  }
  
  // Check if seconds is 0 or negative
  if (timestamp.seconds <= 0) {
    return '-';
  }
  
  try {
    const milliseconds = timestamp.seconds * 1000 + Math.floor(timestamp.nanoseconds / 1e6);
    const date = new Date(milliseconds);
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');
    const second = String(date.getSeconds()).padStart(2, '0');
    
    return `${day}/${month}/${year} ${hour}:${minute}:${second}`;
  } catch (error) {
    console.warn('Error formatting unit timestamp:', error);
    return '-';
  }
};

/**
 * Gets the unit's installation date in formatted string
 * @param {Unit} unit - The unit object
 * @returns {string} - Formatted installation date or '-'
 */
export const getUnitInstallationDate = (unit) => {
  const timestamp = getUnitProperty(unit, 'time_installed');
  if (!timestamp) return '-';
  
  const formatted = formatUnitTimestamp(timestamp);
  return formatted !== '-' ? formatted.split(' ')[0] : '-';
};

/**
 * Gets network information from unit status
 * @param {Unit} unit - The unit object
 * @param {'ethernet'|'wifi'} networkType - Network type
 * @returns {Object|null} - Network information or null
 */
export const getNetworkInfo = (unit, networkType) => {
  const key = networkType === 'ethernet' ? '_ethernet_ip' : '_wifi_ip';
  return getUnitProperty(unit, `statusInfo.${key}`, null);
};

/**
 * Gets hardware/software version information
 * @param {Unit} unit - The unit object
 * @returns {Object} - Version information object
 */
export const getVersionInfo = (unit) => {
  return {
    software: getUnitProperty(unit, 'statusInfo._software_version', '-'),
    hardware: getUnitProperty(unit, 'statusInfo._hardware_version', '-'),
    cartridgeFirmware: getUnitProperty(unit, 'statusInfo._cartridge_firmware_version', '-'),
    cartridgeHardware: getUnitProperty(unit, 'statusInfo._cartridge_hardware_version', '-'),
    scoreScreen: getUnitProperty(unit, 'statusInfo._score_screen_version', '-')
  };
};

/**
 * Gets boolean mode states from unit status
 * @param {Unit} unit - The unit object
 * @returns {Object} - Object with boolean mode states
 */
export const getModeStates = (unit) => {
  return {
    forcedFlushReadings: getUnitProperty(unit, 'statusInfo._forced_waterflush_readings', false),
    periodicFlushActive: getUnitProperty(unit, 'statusInfo._periodic_waterflush_active', false),
    liquidSensorActive: getUnitProperty(unit, 'statusInfo._liquid_sensor_active', false),
    flushButtonActive: getUnitProperty(unit, 'statusInfo._water_flush_button_active', false),
    outOfOrderActive: getUnitProperty(unit, 'statusInfo._out_of_order_active', false),
    welcomeScreenVisible: getUnitProperty(unit, 'statusInfo._welcome_screen_texts_visible', false)
  };
};

/**
 * Gets a default scheduledWF structure for new units
 * @returns {Object} - Default scheduledWF object
 */
export const getDefaultScheduledWF = () => {
  return {
    enabled: false,
    iterations: 1,
    time: "08:00",
    weekDays: {
      Monday: false,
      Tuesday: false,
      Wednesday: false,
      Thursday: false,
      Friday: false,
      Saturday: false,
      Sunday: false
    }
  };
};

export const getDefaultPostAssemblyCalibration = () => {
  return {
    conductivity: {
      currentCalibrationFactor: 1,
      dynamicMode: false,
      enable: false,
      manualScalarFactor: 1
    }
  };
};

export const getDefaultParameters = () => {
  return {
    kPod: {
      justWaterConductivity: 0,
      startThreshold: 0,
      urineConductivity: 0,
      urineHydratedConductivity: 0,
      waterConductivity: 0
    }
  };
};